/**
 * The sign-in buttons on the sign-in page, as in skySchedule. Each one starts
 * the same B2C sign-in with a domain hint, so B2C skips its own picker and goes
 * straight to that provider; an unknown hint just lands on B2C's picker.
 *
 * Which buttons show, and each hint, are build settings:
 *   VITE_SIGN_IN_PROVIDERS            e.g. "microsoft,google,email" (default: all five)
 *   VITE_B2C_DOMAIN_HINT_MICROSOFT    default "commonaad"
 *   VITE_B2C_DOMAIN_HINT_GOOGLE       default "google.com"
 *   VITE_B2C_DOMAIN_HINT_APPLE        default "apple.com"
 *   VITE_B2C_DOMAIN_HINT_OKTA         default "okta.com"
 * "email" (email & password) has no B2C domain: that form is part of B2C's own
 * page, behind a button repeating our choices. It sends `signin=email`, which
 * the Skypoint B2C page (skypoint-azure-b2c, b2clogin_copilot.html) reads to
 * open the form directly (#1479). A page without that support shows its picker.
 */

export type SignInProviderId = "microsoft" | "google" | "apple" | "okta" | "email";

export interface SignInProvider {
  id: SignInProviderId;
  label: string;
  /** Sent with the B2C sign-in request, e.g. `domain_hint` to skip B2C's picker. */
  queryParams: Record<string, string>;
  icon: React.ComponentType<{ className?: string }>;
}

const MicrosoftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 21 21"
    aria-hidden="true"
  >
    <rect
      x="1"
      y="1"
      width="9"
      height="9"
      fill="#f25022"
    />
    <rect
      x="11"
      y="1"
      width="9"
      height="9"
      fill="#7fba00"
    />
    <rect
      x="1"
      y="11"
      width="9"
      height="9"
      fill="#00a4ef"
    />
    <rect
      x="11"
      y="11"
      width="9"
      height="9"
      fill="#ffb900"
    />
  </svg>
);

const GoogleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 48 48"
    aria-hidden="true"
  >
    <path
      fill="#FFC107"
      d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"
    />
  </svg>
);

const AppleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="15"
    height="18"
    viewBox="0 0 384 512"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const OktaIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" />
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="11"
      width="18"
      height="11"
      rx="2"
    />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ALL: Record<SignInProviderId, () => SignInProvider> = {
  microsoft: () => ({
    id: "microsoft",
    label: "Sign in with Microsoft",
    queryParams: { domain_hint: import.meta.env.VITE_B2C_DOMAIN_HINT_MICROSOFT || "commonaad" },
    icon: MicrosoftIcon,
  }),
  google: () => ({
    id: "google",
    label: "Sign in with Google",
    queryParams: { domain_hint: import.meta.env.VITE_B2C_DOMAIN_HINT_GOOGLE || "google.com" },
    icon: GoogleIcon,
  }),
  apple: () => ({
    id: "apple",
    label: "Sign in with Apple",
    queryParams: { domain_hint: import.meta.env.VITE_B2C_DOMAIN_HINT_APPLE || "apple.com" },
    icon: AppleIcon,
  }),
  okta: () => ({
    id: "okta",
    label: "Sign in with Okta",
    queryParams: { domain_hint: import.meta.env.VITE_B2C_DOMAIN_HINT_OKTA || "okta.com" },
    icon: OktaIcon,
  }),
  email: () => ({
    id: "email",
    label: "Sign in with Email & Password",
    queryParams: { signin: "email" },
    icon: LockIcon,
  }),
};

const DEFAULT_ORDER: SignInProviderId[] = ["microsoft", "google", "apple", "okta", "email"];

/** The buttons to show, in the configured order; all five when unset or unreadable. */
export function signInProviders(
  raw: string | undefined = import.meta.env.VITE_SIGN_IN_PROVIDERS,
): SignInProvider[] {
  const wanted = (raw ?? "")
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter((p): p is SignInProviderId => p in ALL);
  const ids = wanted.length > 0 ? [...new Set(wanted)] : DEFAULT_ORDER;
  return ids.map((id) => ALL[id]());
}
